import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, Upload, X } from "lucide-react";
import { toast } from "sonner";

import { API } from "@/config";

// Helper to format BOM quantity display (no conversion needed - everything is in native units)
const formatBOMQuantity = (qty, unit, itemType) => {
  return `${qty.toFixed(2)} ${unit}`;
};

const LogWasteModal = ({ isOpen, onClose, workOrder, component, onSuccess }) => {
  const [lotNumber, setLotNumber] = useState("");
  const [quantity, setQuantity] = useState("");
  const [wasteReason, setWasteReason] = useState("poor_quality");
  const [notes, setNotes] = useState("");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickedLots, setPickedLots] = useState([]);
  const [recentWaste, setRecentWaste] = useState([]);

  useEffect(() => {
    if (isOpen && component) {
      // Get lots that have picks
      const lots = component.lots?.filter(lot => lot.picks && lot.picks.length > 0) || [];
      setPickedLots(lots);

      // Get recent waste entries
      const waste = [];
      component.lots?.forEach(lot => {
        lot.waste?.forEach(w => {
          waste.push({
            ...w,
            lot_number: lot.lot_number
          });
        });
      });
      waste.sort((a, b) => new Date(b.logged_at) - new Date(a.logged_at));
      setRecentWaste(waste.slice(0, 5));
    }
  }, [isOpen, component]);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    
    if (images.length + files.length > 5) {
      toast.error("Maximum 5 images allowed");
      return;
    }

    // Convert to base64 for preview (in production, upload to storage)
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error(`File ${file.name} is too large (max 5MB)`);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImages(prev => [...prev, {
          name: file.name,
          url: reader.result // In production, this would be uploaded to S3/storage
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!lotNumber || !quantity || parseFloat(quantity) <= 0) {
      toast.error("Please select lot number and enter valid quantity");
      return;
    }

    setLoading(true);
    try {
      // In production, upload images to storage first and get URLs
      const imageUrls = images.map(img => img.url); // Placeholder URLs

      await axios.post(`${API}/production/${workOrder.wo_id}/log-waste`, null, {
        params: {
          component_id: component.component_id,
          lot_number: lotNumber,
          quantity: parseFloat(quantity),
          waste_reason: wasteReason,
          logged_by: "production",
          notes: notes,
          waste_images: imageUrls
        }
      });
      
      toast.success("Waste logged successfully");
      setLotNumber("");
      setQuantity("");
      setWasteReason("poor_quality");
      setNotes("");
      setImages([]);
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error logging waste:", error);
      toast.error(error.response?.data?.detail || "Failed to log waste");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Log Waste - {component?.component_name}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Lot Number Selection */}
          <div>
            <Label htmlFor="lot-number">Lot Number</Label>
            <select
              id="lot-number"
              value={lotNumber}
              onChange={(e) => setLotNumber(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mt-1"
              required
            >
              <option value="">Select lot to log waste from...</option>
              {pickedLots.map((lot, idx) => (
                <option key={idx} value={lot.lot_number}>
                  {lot.lot_number} (Picked: {formatBOMQuantity(lot.picks?.reduce((sum, p) => sum + p.picked_qty, 0), component?.unit, component?.item_type)})
                </option>
              ))}
            </select>
            {pickedLots.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                ⚠️ No lots have been picked yet. Pick inventory first.
              </p>
            )}
          </div>

          {/* Quantity Input */}
          <div>
            <Label htmlFor="quantity">Quantity ({component?.unit})</Label>
            <Input
              id="quantity"
              type="number"
              step="0.01"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Enter quantity wasted"
              required
            />
          </div>

          {/* Waste Reason Selection */}
          <div>
            <Label htmlFor="waste-reason">Waste Reason</Label>
            <select
              id="waste-reason"
              value={wasteReason}
              onChange={(e) => setWasteReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mt-1"
              required
            >
              <option value="poor_quality">Poor Quality</option>
              <option value="food_waste">Food Waste</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional details about the waste..."
              rows={3}
            />
          </div>

          {/* Image Upload */}
          <div>
            <Label>Images (Max 5)</Label>
            <div className="mt-2">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
                disabled={images.length >= 5}
              />
              <label htmlFor="image-upload">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  as="span"
                  disabled={images.length >= 5}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Images ({images.length}/5)
                </Button>
              </label>
            </div>

            {/* Image Previews */}
            {images.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-3">
                {images.map((image, idx) => (
                  <div key={idx} className="relative group">
                    <img
                      src={image.url}
                      alt={image.name}
                      className="w-full h-24 object-cover rounded border"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(idx)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Waste History */}
          {recentWaste.length > 0 && (
            <div>
              <Label className="mb-2 block">Recent Waste Entries</Label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {recentWaste.map((waste, idx) => (
                  <Card key={idx} className="bg-gray-50">
                    <CardContent className="p-3">
                      <div className="flex justify-between items-start text-sm">
                        <div>
                          <div className="font-mono font-semibold">{waste.lot_number}</div>
                          <div className="text-xs text-gray-600">
                            {formatBOMQuantity(waste.waste_qty, component?.unit, component?.item_type)} • {waste.waste_reason.replace('_', ' ')} • {waste.logged_by}
                          </div>
                          {waste.notes && (
                            <div className="text-xs text-gray-500 italic mt-1">{waste.notes}</div>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(waste.logged_at).toLocaleString()}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 justify-end pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || pickedLots.length === 0}>
              {loading ? "Logging..." : "Log Waste"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LogWasteModal;
